import { useState, useEffect, useRef, useCallback } from "react"
import { Product } from "../Apis/productApis"
import { message } from "antd"
import { fetchApprovedStores } from "../ReduxToolKit/Slice/StoreSlice"
import { useDispatch } from "react-redux"
import { translations } from "../utils/Translation/staticTranslation"

const useProducts = (
  selectedStore,
  selectedCategory,
  searchTerm,
  filterStatus,
  closeEditModal,
  selectedProduct,
  setDeleteModalVisible
) => {
  const dispatch = useDispatch()
  const isFirstRender = useRef(true)
  const prevStoreId = useRef(null)
  // Monotonic id so only the most recent fetch can update state. Prevents a
  // slow earlier request (e.g. page 1) from clobbering a newer one (page 2)
  // when responses arrive out of order — the cause of pagination "sticking"
  // on stage/prod but not locally.
  const requestIdRef = useRef(0)

  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [updateLoading, setUpdateLoading] = useState({})
  const [deleteLoading, setDeleteLoading] = useState(false)

  const lang = localStorage.getItem("language") || "ar"
  const { common_error_msg } = translations[lang]

  // =====================
  // Fetch product list
  // =====================
  const fetchProductList = useCallback(
    async (page, categoryArg, termArg, statusArg) => {
      const reqId = ++requestIdRef.current
      setLoading(true)
      const payload = {}

      if (categoryArg === "Name" && termArg) {
        payload.name_english = termArg
      }

      if (categoryArg === "Category" && termArg) {
        payload.category = termArg
      }

      if (statusArg !== undefined && statusArg !== "") {
        payload.is_active = statusArg
      }

      if (selectedStore?.id) {
        payload.store_id = selectedStore.id
      }

      try {
        const response = await Product.getProductList(payload, page)
        // Ignore if a newer fetch has been issued since this one started.
        if (reqId !== requestIdRef.current) return
        if (response.status === 200) {
          setProducts(response.data)
        }
      } catch (error) {
        if (reqId !== requestIdRef.current) return
        console.error("Error:", error)
        message.error(common_error_msg?.failed_to_fetched_products_try_again)
      } finally {
        // Only the latest request controls the loading flag.
        if (reqId === requestIdRef.current) setLoading(false)
      }
    },
    [selectedStore?.id, common_error_msg]
  )

  const refetchProducts = useCallback(() => {
    fetchProductList(currentPage, selectedCategory, searchTerm, filterStatus)
  }, [
    fetchProductList,
    currentPage,
    selectedCategory,
    searchTerm,
    filterStatus,
  ])

  // =====================
  // Delete product
  // =====================
  const handleDeleteProduct = async (row) => {
    setDeleteLoading(true)
    const payload = {
      product_ids: [row?.id], // ✅ use uuid for product
      store: row?.store,
    }

    if (!payload.store) {
      message.error(common_error_msg?.select_store_first)
      setDeleteLoading(false)
      return
    }

    try {
      const response = await Product.deleteProduct(payload)
      if (response.status === 204) {
        await fetchProductList(
          currentPage,
          selectedCategory,
          searchTerm,
          filterStatus
        )
      }
      setDeleteModalVisible(false)
    } catch (error) {
      console.error("Error:", error)
      message.error("Failed to delete product")
    } finally {
      setDeleteLoading(false)
    }
  }

  // =====================
  // Update product
  // =====================
  const handleUpdateProduct = async (
    values,
    selectedProduct,
    row = null,
    status
  ) => {
    try {
      const productId = selectedProduct?.id || row?.id

      if (!productId) {
        console.error("❌ No productId (uuid) found for update")
        return
      }

      // Ensure store is present
      const storeId = selectedStore?.id || selectedProduct?.store || row?.store
      if (!storeId) {
        message.error(common_error_msg?.select_store_first)
        return
      }

      setUpdateLoading((prev) => ({ ...prev, [productId]: true }))

      // Build form data
      const formData = new FormData()
      formData.append("store", storeId)
      formData.append(
        "is_active",
        status !== undefined ? status : selectedProduct?.is_active ?? true
      )
      formData.append(
        "product_name",
        values[`product_name_1`] || row?.name_english
      )
      formData.append("category", values[`category_1`] || row?.category)
      formData.append("price_unit", values[`price_unit_1`] || row?.price_unit)
      formData.append(
        "price_amount",
        values[`price_amount_1`] || row?.price_amount
      )
      formData.append("image_url", values[`image_url_1`] || row?.image_url)
      formData.append(
        "product_url",
        values[`product_url_1`] || row?.product_url
      )

      // API call
      const response = await Product.updateProduct(formData, productId)

      if (response.status === 200) {
        message.success(common_error_msg?.product_updated_successfully)
        await fetchProductList(
          currentPage,
          selectedCategory,
          searchTerm,
          filterStatus
        )
        closeEditModal()
      }
    } catch (error) {
      console.error("Failed to update product:", error)
    } finally {
      setUpdateLoading((prev) => ({
        ...prev,
        [selectedProduct?.id || row?.id]: false,
      }))
    }
  }

  // =====================
  // Effects
  // =====================
  useEffect(() => {
    dispatch(fetchApprovedStores())
  }, [dispatch])

  // Track previous filter values to detect changes
  const prevFilters = useRef({
    selectedCategory,
    searchTerm,
    filterStatus,
  })

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      // Record the initial store id so the store-change check below doesn't
      // false-trigger on the first pagination click. Without this, prevStoreId
      // stays `null` while `selectedStore?.id` is `undefined` (no store picked),
      // and `null !== undefined` wrongly resets currentPage back to 1.
      prevStoreId.current = selectedStore?.id
      fetchProductList(1, selectedCategory, searchTerm, filterStatus)
      prevFilters.current = { selectedCategory, searchTerm, filterStatus }
      return
    }

    if (prevStoreId.current !== selectedStore?.id) {
      prevStoreId.current = selectedStore?.id
      setCurrentPage(1)
      fetchProductList(1, selectedCategory, searchTerm, filterStatus)
      prevFilters.current = { selectedCategory, searchTerm, filterStatus }
      return
    }

    // Check if any filter changed (not just store or page)
    const filtersChanged =
      prevFilters.current.selectedCategory !== selectedCategory ||
      prevFilters.current.searchTerm !== searchTerm ||
      prevFilters.current.filterStatus !== filterStatus

    // Reset to page 1 if filters changed
    if (filtersChanged) {
      setCurrentPage(1)
      prevFilters.current = { selectedCategory, searchTerm, filterStatus }
      const handler = setTimeout(() => {
        fetchProductList(1, selectedCategory, searchTerm, filterStatus)
      }, 300)
      return () => clearTimeout(handler)
    }

    // If only page changed (not filters), fetch with current page
    const handler = setTimeout(() => {
      fetchProductList(currentPage, selectedCategory, searchTerm, filterStatus)
    }, 300)

    return () => clearTimeout(handler)
  }, [
    fetchProductList,
    selectedStore?.id,
    selectedCategory,
    searchTerm,
    filterStatus,
    currentPage,
  ])

  // =====================
  // Return API
  // =====================
  return {
    setCurrentPage,
    updateLoading,
    handleUpdateProduct,
    handleDeleteProduct,
    refetchProducts,
    products,
    loading,
    currentPage,
    deleteLoading,
  }
}

export default useProducts
